import { apiClient } from "./client";
import type { UUID } from "./types";

/**
 * Single round-trip Analytics endpoint.
 *
 * The page composes ~10 sections — funnel, trends, team table,
 * leaderboard, breakdowns, deal attribution, etc. — and the backend
 * is expected to compute everything for a given (period, owner) pair
 * and return the bundle in one shot. Saves the page from issuing a
 * dozen parallel requests and lets the backend share the same time-
 * window calculation across sections.
 *
 * The response shape mirrors the UI sections 1:1 so a backend
 * implementer can map straight from sections of this contract to
 * SQL views.
 */

export type AnalyticsPeriod = "7d" | "30d" | "90d" | "12m" | "custom";

export interface AnalyticsParams {
  period?: AnalyticsPeriod;
  /** ISO date — used when period === "custom". */
  start?: string;
  end?: string;
  /** UUID of the project member, or "all" / undefined for everyone. */
  owner_id?: string;
}

export interface KpiPoint {
  /** Bucket label — week or month start, ISO date. */
  at: string;
  value: number;
}

export interface KpiSummary {
  /** Stable key for the KPI: "new" | "qualified" | "contacted" |
   *  "replied" | "won" | "conv_rate" — drives icon + label per UI
   *  section 1. */
  key: string;
  label: string;
  value: number;
  /** Optional change vs previous comparable window (-100…∞). */
  delta_pct?: number | null;
  /** Optional sparkline series — same time bucket cadence as the
   *  trends section. */
  sparkline?: KpiPoint[];
  /** Conv-rate KPI flips this so the card formats as "5.3%". */
  format?: "count" | "percentage";
}

export interface FunnelStageDetail {
  key: string;
  label: string;
  order: number;
  count: number;
  /** Percentage of the previous stage's count. The first stage
   *  reports 100. */
  percentage_of_previous: number;
  /** IANA-style color name pulled from the project's pipeline
   *  config (TAG_COLORS keys: blue / amber / emerald / etc). */
  color?: string | null;
}

export interface ConversionMetric {
  key:
    | "avg_time_to_first_reply"
    | "avg_time_to_won"
    | "reply_rate"
    | "win_rate";
  label: string;
  /** Value rendered as the card's primary number. Server pre-
   *  formats (3.2 / 21.5 / 12.7) — UI appends the suffix. */
  value: number;
  /** "days" | "%" — kept short, the UI prints verbatim. */
  unit: "days" | "%";
  delta_pct?: number | null;
}

export interface TrendsBucket {
  at: string;
  /** Counts per pipeline stage in the bucket. Keys match the
   *  pipeline config ("new" / "qualified" / "contacted" / "replied"
   *  / "won" / etc). */
  counts: Record<string, number>;
}

export interface OutreachStat {
  key: "sent" | "open_rate" | "click_rate" | "reply_rate";
  label: string;
  value: number;
  unit: "count" | "%";
  /** % delta vs the equivalent previous window. */
  delta_pct?: number | null;
  /** Mini bar chart series — usually 6-8 buckets. */
  sparkline?: KpiPoint[];
}

export interface TeamRow {
  member_id: UUID;
  member_name: string;
  member_email?: string | null;
  /** UI uses the colour to render a 20px coloured avatar fallback. */
  avatar_color?: string | null;
  companies: number;
  contacted: number;
  replied: number;
  won: number;
  calls: number;
  /** Total call duration in seconds — UI formats to "Xh Ym". */
  call_seconds: number;
  tasks_done: number;
  /** 0–1; UI renders as a percentage. */
  conversion_rate: number;
}

export interface BreakdownEntry {
  key: string;
  label: string;
  count: number;
  /** Optional secondary metric for breakdowns that surface a
   *  conversion rate (e.g. by founder origin). */
  conversion_rate?: number | null;
}

export interface RecentWin {
  company_id: number;
  company_name: string;
  /** Sector label used as the chip beside the name. */
  sector?: string | null;
  won_at: string;
  owner_name?: string | null;
  /** Days from first activity to won — drives the duration label. */
  cycle_days?: number | null;
}

/** Leaderboard entry — used by all four podiums on section 5b. */
export interface LeaderboardEntry {
  member_id: UUID;
  member_name: string;
  avatar_color?: string | null;
  /** Primary value rendered bold — count for calls/replies/wins,
   *  the action total for activity. */
  primary_value: number;
  /** Pre-formatted muted secondary line (e.g. "5h 20m" or "41.7%
   *  rate" or "2.2% conv"). UI prints verbatim. */
  secondary_label?: string | null;
  /** Optional period-over-period delta (positive = improvement on
   *  this metric). Rendered in emerald / rose / slate. */
  delta?: number | null;
}

export interface LeaderboardBoard {
  key: "calls" | "replies" | "wins" | "activity";
  title: string;
  emoji: string;
  /** Subtitle shown below the metric name (e.g. "by call duration"). */
  subtitle?: string | null;
  entries: LeaderboardEntry[];
}

/** A single step in a deal's journey through the pipeline — drives
 *  the horizontal timeline in section 8. */
export interface DealJourneyStep {
  key: string;
  label: string;
  /** Pipeline color name. */
  color?: string | null;
  occurred_at: string;
  /** "engine" | "user" | "prospect" — picks the icon glyph. */
  actor_kind: "engine" | "user" | "prospect";
  actor_name: string;
  /** UUID of the responsible member, when actor_kind === "user". */
  actor_id?: string | null;
  /** Avatar color for "user" actors. */
  avatar_color?: string | null;
}

export interface DealContributor {
  member_id: UUID;
  member_name: string;
  avatar_color?: string | null;
  /** "Closer" / "Qualifier" / "Sourcer" / "Engine" — drives the
   *  badge tone (emerald / amber / slate / slate respectively). */
  role: string;
  /** Free-text summary — "3 calls (1h 05m)" / "Found, qualified,
   *  contacted" — UI prints verbatim. */
  summary: string;
}

export interface DealAttributionEntry {
  company_id: number;
  company_name: string;
  sector?: string | null;
  won_at: string;
  cycle_days: number;
  steps: DealJourneyStep[];
  contributors: DealContributor[];
  stats?: {
    calls?: number;
    emails?: number;
    /** "First reply in 3 days" — pre-formatted. */
    first_reply_label?: string | null;
  };
}

/** Object form of the leaderboard the backend actually ships. The UI
 *  fans this out into the four-up board layout (Calls / Replies /
 *  Wins / Activity) — see AnalyticsPage where it's mapped onto
 *  `LeaderboardBoard[]`. */
export interface LeaderboardObject {
  by_calls?: LeaderboardEntry[];
  by_replies?: LeaderboardEntry[];
  by_wins?: LeaderboardEntry[];
  by_activity?: LeaderboardEntry[];
}

export interface AnalyticsResponse {
  /** Resolved [start, end] window — the backend echoes back the
   *  timestamps so the UI can label the period header even for
   *  custom selections. */
  period: {
    key: AnalyticsPeriod;
    start: string;
    end: string;
  };
  kpis?: KpiSummary[];
  funnel?: FunnelStageDetail[];
  conversion?: ConversionMetric[];
  trends?: {
    /** Series keys ↔ pipeline stage colors. */
    series: Array<{ key: string; label: string; color?: string | null }>;
    buckets: TrendsBucket[];
  };
  outreach?: OutreachStat[];
  /** Per-member team performance rows. The backend ships this under
   *  `by_owner`; an older shape used `team`, which we still accept
   *  as a fallback so a half-deployed environment doesn't blank the
   *  table. */
  by_owner?: TeamRow[];
  /** @deprecated use `by_owner`. */
  team?: TeamRow[];
  /** Leaderboard ships as an object with the four metric arrays —
   *  the page maps it onto the LeaderboardBoard[] shape the
   *  Leaderboard component expects. */
  leaderboard?: LeaderboardObject;
  /** @deprecated legacy array form, accepted as a fallback. */
  leaderboards?: LeaderboardBoard[];
  /** Breakdowns ship as flat top-level fields (per the canonical
   *  contract). The nested `breakdowns` form is accepted as a
   *  fallback for older backends. */
  by_source?: BreakdownEntry[];
  by_sector?: BreakdownEntry[];
  by_founder_origin?: BreakdownEntry[];
  /** @deprecated nested form. */
  breakdowns?: {
    by_source?: BreakdownEntry[];
    by_sector?: BreakdownEntry[];
    by_founder_origin?: BreakdownEntry[];
  };
  recent_wins?: RecentWin[];
  /** Canonical name for the won-deal journey list. */
  deal_attribution?: DealAttributionEntry[];
  /** @deprecated use `deal_attribution`. */
  deals?: DealAttributionEntry[];
}

export async function getAnalytics(
  slug: string,
  params: AnalyticsParams = {},
): Promise<AnalyticsResponse> {
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    cleaned[key] = value;
  }
  const res = await apiClient.get<unknown>(
    `/projects/${slug}/analytics`,
    { params: cleaned },
  );
  return normalizeAnalyticsResponse(res.data);
}

/**
 * Normalize the backend's response into the shape every analytics
 * component expects.
 *
 * Two recurring sources of bugs we collapse here:
 *
 *   1. *Field aliases* — a single concept ships under different
 *      names depending on the section (`member_name` vs `owner_name`
 *      vs `user_name`, `cycle_days` vs `total_duration_days`,
 *      `steps` vs `journey`, breakdown labels under `name` /
 *      `source` / `sector` / `origin`, etc.). Components used to
 *      read one canonical name and silently render "—" / "Item N"
 *      when the backend used another. Here we coalesce through every
 *      known alias before the data leaves the API layer.
 *
 *   2. *Percentage scaling* — some endpoints return ratios in 0..1,
 *      others in 0..100. Components multiply by 100 once for display
 *      so a backend that already pre-multiplied produced absurd
 *      values like "2220.0%". `normalizePct` detects the scale and
 *      returns a 0..1 ratio, which is what consumers all assume.
 */

const FUNNEL_FALLBACK_COLORS = [
  "slate",
  "blue",
  "amber",
  "violet",
  "emerald",
  "rose",
];

type AnyRecord = Record<string, unknown>;
const asRecord = (v: unknown): AnyRecord =>
  v && typeof v === "object" ? (v as AnyRecord) : {};
const asArray = <T = unknown>(v: unknown): T[] =>
  Array.isArray(v) ? (v as T[]) : [];
const asString = (v: unknown, fallback = ""): string =>
  typeof v === "string" ? v : fallback;
const asNumber = (v: unknown, fallback = 0): number =>
  typeof v === "number" && Number.isFinite(v) ? v : fallback;
const asNullableString = (v: unknown): string | null =>
  typeof v === "string" && v.length > 0 ? v : null;

/** Map either ratio (0..1) or percentage (0..100) onto a 0..1 ratio.
 *  Display layers multiply by 100 once. Anything > 1 is treated as
 *  already-percentage. */
function normalizePct(v: unknown): number {
  if (typeof v !== "number" || !Number.isFinite(v)) return 0;
  return v > 1 ? v / 100 : v;
}

function pickFirstString(...candidates: unknown[]): string {
  for (const c of candidates) {
    if (typeof c === "string" && c.length > 0) return c;
  }
  return "";
}

function normalizeFunnel(raw: unknown): FunnelStageDetail[] {
  const list = asArray<AnyRecord>(raw);
  // Pre-resolve counts so we can compute % of previous when the
  // backend doesn't ship it.
  const stages = list.map((s, i) => ({
    raw: s,
    order: asNumber(s.order, i),
    count: asNumber(s.count),
  }));
  stages.sort((a, b) => a.order - b.order);
  return stages.map((entry, i) => {
    const s = entry.raw;
    const prevCount = i === 0 ? entry.count : stages[i - 1].count;
    const computedPct =
      i === 0
        ? 100
        : prevCount > 0
          ? (entry.count / prevCount) * 100
          : 0;
    return {
      key: pickFirstString(
        s.key,
        s.status,
        s.status_key,
        `stage-${i}`,
      ),
      label: pickFirstString(
        s.label,
        s.status_label,
        s.name,
        s.status,
        `Stage ${i + 1}`,
      ),
      order: entry.order,
      count: entry.count,
      percentage_of_previous: asNumber(
        s.percentage_of_previous ?? s.conversion_from_previous,
        computedPct,
      ),
      // Fall back to the per-index palette colour so bars never all
      // collapse to slate when the backend omits the colour field.
      color:
        asNullableString(s.color) ??
        asNullableString(s.status_color) ??
        FUNNEL_FALLBACK_COLORS[i % FUNNEL_FALLBACK_COLORS.length],
    };
  });
}

function normalizeTeamRow(raw: unknown, idx: number): TeamRow {
  const r = asRecord(raw);
  return {
    member_id: pickFirstString(
      r.member_id,
      r.owner_id,
      r.user_id,
      r.id,
      `member-${idx}`,
    ),
    member_name: pickFirstString(
      r.member_name,
      r.owner_name,
      r.user_name,
      r.name,
      r.full_name,
      "—",
    ),
    member_email:
      asNullableString(r.member_email) ??
      asNullableString(r.email) ??
      asNullableString(r.owner_email),
    avatar_color:
      asNullableString(r.avatar_color) ??
      asNullableString(r.color),
    // Canonical names the backend now ships (`*_count`); legacy
    // aliases (`*_owned` / `*_logged` / bare short names) kept as
    // fallbacks so the page survives a half-rolled deployment.
    companies: asNumber(
      r.companies_count ?? r.companies_owned ?? r.companies,
    ),
    contacted: asNumber(r.contacted_count ?? r.contacted),
    replied: asNumber(r.replied_count ?? r.replied),
    won: asNumber(r.won_count ?? r.won),
    calls: asNumber(r.calls_count ?? r.calls_logged ?? r.calls),
    call_seconds: asNumber(
      r.call_seconds ??
        r.call_duration_seconds ??
        r.calls_duration_seconds,
    ),
    tasks_done: asNumber(
      r.tasks_done_count ?? r.tasks_done ?? r.tasks_completed,
    ),
    conversion_rate: normalizePct(r.conversion_rate ?? r.conv_rate),
  };
}

function normalizeLeaderboardEntry(
  raw: unknown,
  idx: number,
): LeaderboardEntry {
  const e = asRecord(raw);
  return {
    member_id: pickFirstString(
      e.member_id,
      e.owner_id,
      e.user_id,
      e.id,
      `entry-${idx}`,
    ),
    member_name: pickFirstString(
      e.member_name,
      e.owner_name,
      e.user_name,
      e.name,
      e.full_name,
      "—",
    ),
    avatar_color:
      asNullableString(e.avatar_color) ?? asNullableString(e.color),
    // Primary value can ship under several names depending on the
    // metric the board surfaces — coalesce the canonical *_count
    // names the aligned backend uses, then the generic value/count
    // fields legacy responses used.
    primary_value: asNumber(
      e.primary_value ??
        e.value ??
        e.count ??
        e.calls_count ??
        e.replies_count ??
        e.wins_count ??
        e.activity_count,
    ),
    secondary_label:
      asNullableString(e.secondary_label) ??
      asNullableString(e.subtitle) ??
      asNullableString(e.secondary),
    delta:
      typeof e.delta === "number" && Number.isFinite(e.delta)
        ? e.delta
        : null,
  };
}

function normalizeLeaderboard(raw: unknown): LeaderboardObject | undefined {
  if (!raw) return undefined;
  const lb = asRecord(raw);
  return {
    by_calls: asArray(lb.by_calls).map(normalizeLeaderboardEntry),
    by_replies: asArray(lb.by_replies).map(normalizeLeaderboardEntry),
    by_wins: asArray(lb.by_wins).map(normalizeLeaderboardEntry),
    by_activity: asArray(lb.by_activity).map(normalizeLeaderboardEntry),
  };
}

/** primaryField names the natural label slot for this breakdown
 *  (e.g. `source` for the source pie, `sector` for the sector bar)
 *  so we coalesce the right one when label/name aren't set. */
function normalizeBreakdown(
  raw: unknown,
  primaryField: "source" | "sector" | "origin" | "founder_origin",
): BreakdownEntry[] {
  return asArray<AnyRecord>(raw).map((e, i) => {
    const label = pickFirstString(
      e.label,
      e.name,
      e[primaryField],
      e.title,
      e.key,
      `Item ${i + 1}`,
    );
    const key = pickFirstString(e.key, e[primaryField], e.name, label);
    return {
      key,
      label,
      count: asNumber(e.count),
      conversion_rate:
        e.conversion_rate != null
          ? normalizePct(e.conversion_rate)
          : null,
    };
  });
}

function normalizeJourneyStep(
  raw: unknown,
  idx: number,
): DealJourneyStep {
  const s = asRecord(raw);
  return {
    key: pickFirstString(s.key, s.status, s.stage, `step-${idx}`),
    label: pickFirstString(
      s.label,
      s.status_label,
      s.name,
      s.status,
      s.stage,
      `Step ${idx + 1}`,
    ),
    color:
      asNullableString(s.color) ??
      asNullableString(s.status_color) ??
      FUNNEL_FALLBACK_COLORS[idx % FUNNEL_FALLBACK_COLORS.length],
    occurred_at: asString(s.occurred_at ?? s.at ?? s.timestamp),
    actor_kind: ((): "engine" | "user" | "prospect" => {
      const k = asString(s.actor_kind ?? s.actor_type ?? s.kind ?? s.type);
      if (k === "engine" || k === "system") return "engine";
      if (k === "prospect" || k === "lead") return "prospect";
      return "user";
    })(),
    actor_name: pickFirstString(
      s.actor_name,
      s.actor,
      s.user_name,
      s.owner_name,
      "—",
    ),
    actor_id:
      asNullableString(s.actor_id) ??
      asNullableString(s.user_id) ??
      asNullableString(s.owner_id),
    avatar_color:
      asNullableString(s.avatar_color) ?? asNullableString(s.color),
  };
}

function normalizeContributor(
  raw: unknown,
  idx: number,
): DealContributor {
  const c = asRecord(raw);
  return {
    member_id: pickFirstString(
      c.member_id,
      c.user_id,
      c.owner_id,
      c.id,
      `contrib-${idx}`,
    ),
    member_name: pickFirstString(
      c.member_name,
      c.user_name,
      c.owner_name,
      c.name,
      c.full_name,
      "—",
    ),
    avatar_color:
      asNullableString(c.avatar_color) ?? asNullableString(c.color),
    role: pickFirstString(c.role, c.kind, c.type, "Contributor"),
    summary: asString(c.summary ?? c.description ?? ""),
  };
}

function normalizeDeal(raw: unknown, idx: number): DealAttributionEntry {
  const d = asRecord(raw);
  const steps = asArray(d.steps ?? d.journey).map(normalizeJourneyStep);
  const contributors = asArray(d.contributors).map(normalizeContributor);
  return {
    company_id: asNumber(d.company_id ?? d.id, idx),
    company_name: asString(d.company_name ?? d.name, "—"),
    sector: asNullableString(d.sector),
    won_at: asString(d.won_at ?? d.closed_at),
    cycle_days: asNumber(
      d.cycle_days ?? d.total_duration_days ?? d.duration_days,
    ),
    steps,
    contributors,
    stats: d.stats ? (d.stats as DealAttributionEntry["stats"]) : undefined,
  };
}

function normalizeAnalyticsResponse(raw: unknown): AnalyticsResponse {
  const r = asRecord(raw);
  const period = asRecord(r.period);
  return {
    period: {
      key: (period.key as AnalyticsPeriod) ?? "30d",
      start: asString(period.start),
      end: asString(period.end),
    },
    kpis: asArray<AnyRecord>(r.kpis).map((k) => ({
      key: pickFirstString(k.key, k.id, ""),
      label: pickFirstString(k.label, k.name, ""),
      value: asNumber(k.value),
      delta_pct:
        typeof k.delta_pct === "number" ? k.delta_pct : null,
      sparkline: asArray<AnyRecord>(k.sparkline).map((p) => ({
        at: asString(p.at),
        value: asNumber(p.value),
      })),
      format: k.format === "percentage" ? "percentage" : "count",
    })),
    funnel: normalizeFunnel(r.funnel),
    conversion: asArray<AnyRecord>(r.conversion).map((c) => ({
      key: c.key as ConversionMetric["key"],
      label: pickFirstString(c.label, c.key as string, ""),
      value: asNumber(c.value),
      unit: c.unit === "%" ? "%" : "days",
      delta_pct:
        typeof c.delta_pct === "number" ? c.delta_pct : null,
    })),
    trends: r.trends
      ? (r.trends as AnalyticsResponse["trends"])
      : undefined,
    outreach: asArray<AnyRecord>(r.outreach).map((o) => ({
      key: o.key as OutreachStat["key"],
      label: pickFirstString(o.label, o.key as string, ""),
      value: asNumber(o.value),
      unit: o.unit === "%" ? "%" : "count",
      delta_pct:
        typeof o.delta_pct === "number" ? o.delta_pct : null,
      sparkline: asArray<AnyRecord>(o.sparkline).map((p) => ({
        at: asString(p.at),
        value: asNumber(p.value),
      })),
    })),
    by_owner: asArray(r.by_owner ?? r.team).map(normalizeTeamRow),
    leaderboard: normalizeLeaderboard(r.leaderboard),
    by_source: normalizeBreakdown(
      r.by_source ?? asRecord(r.breakdowns).by_source,
      "source",
    ),
    by_sector: normalizeBreakdown(
      r.by_sector ?? asRecord(r.breakdowns).by_sector,
      "sector",
    ),
    by_founder_origin: normalizeBreakdown(
      r.by_founder_origin ?? asRecord(r.breakdowns).by_founder_origin,
      "founder_origin",
    ),
    recent_wins: asArray<AnyRecord>(r.recent_wins).map((w) => ({
      company_id: asNumber(w.company_id),
      company_name: asString(w.company_name ?? w.name, "—"),
      sector: asNullableString(w.sector),
      won_at: asString(w.won_at ?? w.closed_at),
      owner_name:
        asNullableString(w.owner_name) ??
        asNullableString(w.user_name) ??
        asNullableString(w.member_name),
      cycle_days: asNumber(w.cycle_days ?? w.total_duration_days, 0),
    })),
    deal_attribution: asArray(r.deal_attribution ?? r.deals).map(
      normalizeDeal,
    ),
  };
}
